<template>
  <li
    :id="trackingIdFacetItem"
    :class="{
      'f-list-item--selected': selected,
      'f-list-item--color': isColor,
    }"
    class="f-list-item"
    tabindex="0"
    :aria-labelledby="`facet-item-label-${label}`"
    @keydown.enter="$emit('change', !selected)"
    @keydown.space.prevent="$emit('change', !selected)"
  >
    <component
      :is="isLink && indexable ? 'a' : 'span'"
      :href="isLink && indexable ? combinedHref : undefined"
      class="f-list-item--link"
    >
      <label
        :class="{
          'f-list-item-label--color': isColor,
        }"
        class="f-list-item-label"
      >
        <span
          v-if="!isChildFacet && !isColor"
          class="f-list-item-icon-container"
        >
          <fn-icon
            v-if="selected"
            color="white"
            filled
            icon="checkmark-thin"
            size="s"
          />
        </span>
        <input
          v-if="!isChildFacet"
          :value="selected"
          class="checkbox"
          type="checkbox"
          @change="$emit('change', !selected)"
        />
        <fn-checkbox-toggle
          v-else
          :id="trackingIdFacetItemToggle"
          :label="labelWithCount"
          :value="selected"
          @update:value="$emit('change', !selected)"
        />
        <span
          v-if="isColor"
          :style="colorStyle"
          class="item-color"
        >
          <fn-icon
            v-if="selected"
            color="green-light"
            icon="checkmark-thin"
            size="s"
          />
        </span>
        <span
          v-if="!isChildFacet"
          :id="`facet-item-label-${label}`"
          class="item-text"
          v-html="labelWithCount"
        />
      </label>
      <slot />
    </component>
  </li>
</template>

<script>
export default {
  props: {
    label: {
      type: String,
      required: true,
    },
    count: {
      type: [String, Number],
      default: null,
    },
    selected: {
      type: Boolean,
    },
    colorCode: {
      type: String,
      default: '',
    },
    href: {
      type: String,
      default: undefined,
    },
    val: {
      type: String,
      required: true,
    },
    isLink: {
      type: Boolean,
    },
    facet: {
      type: String,
      default: '',
    },
    indexable: {
      type: Boolean,
    },
    isChildFacet: {
      type: Boolean,
    },
  },
  emits: ['change'],
  computed: {
    isColor() {
      return !!this.colorCode;
    },

    colorStyle() {
      return getColorStyleCss(this.colorCode);
    },

    combinedHref() {
      //  Build query string with current path and code + value
      return this.$t('facets.urlTemplate', {
        path: this.$router.currentRoute.path,
        facet: this.facet,
        value: encodeURIComponent(this.val),
      });
    },

    labelWithCount() {
      if (this.count) {
        return `${this.label} (${this.count})`;
      }

      return this.$t(`${this.label}`);
    },

    trackingIdFacetItem() {
      return this.createTrackingId('facet-item');
    },

    trackingIdFacetItemToggle() {
      return this.createTrackingId('facet-item-toggle');
    },
  },
  methods: {
    /**
     * Returns a tracking ID in the right format that contains all relevant data
     * @param value {String}
     * @returns {string}
     */
    createTrackingId(value) {
      const trackingIdParts = [value, this.facet, this.val];

      if (this.isChildFacet) {
        trackingIdParts.unshift('child');
      }

      return trackingIdParts.join('-');
    },
  },
};
</script>

<style lang="scss" scoped>
.f-list-item {
  min-height: 40px;
  flex-shrink: 0;
  list-style: none;
  font-size: map-get($font-sizes, 'm');
  border-bottom: 1px solid palette(sand);
  position: relative;

  &:focus-visible {
    @include focus-outline-layered;
  }

  .f-list-item--link {
    text-decoration: none;
    color: palette(black);

    &:active,
    &:visited {
      color: palette(black);
    }
  }

  .facet--desktop & {
    border-bottom: none;
  }

  .f-list-item-label {
    display: flex;
    align-items: center;
    gap: $pad;
    position: relative;
    width: 100%;
    padding: $pad $pad * 2 $pad $grid-margin-width;
    cursor: pointer;

    &:hover {
      text-decoration: none;
      background: palette(sand, light);
    }

    .item-text {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .icon {
      font-size: map-get($font-sizes, 'l');
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    .facet--desktop & {
      padding: math.div($pad, 2) $pad * 2 math.div($pad, 2) $pad;
    }
  }

  .f-list-item-label--color {
    flex-direction: column;
    gap: 0;

    .item-text {
      text-overflow: initial;
      white-space: normal;
      line-height: 1.2;
      padding-top: math.div($pad, 2);
    }
  }

  .f-list-item-icon-container {
    border: 2px solid palette(grey, metallic);
    border-radius: 8px;
    font-weight: 600;
    position: relative;
    width: 32px;
    height: 32px;
  }

  .checkbox {
    display: none;
  }

  &.f-list-item--color {
    width: 50px;
    margin: $pad $pad 0;
    border-bottom: none;

    .facet--desktop & {
      margin: math.div($pad, 2) math.div($pad, 1.4) math.div($pad, 2);
    }

    .f-list-item-label {
      padding: 0;
      font-size: map-get($font-sizes, 's');
      line-height: 24px;
      text-align: center;

      .item-color {
        width: 50px;
        height: 50px;
        display: block;
        border-radius: map-get($border-radiuses, 'l');
        box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
      }

      .icon {
        left: 50%;
        transform: translate(-50%, -50%);
        color: white;
        text-shadow: 0 0 2px rgba(0, 0, 0, 1);
      }

      &:hover {
        background-color: transparent;
      }
    }
  }

  &.f-list-item--selected {
    &.f-list-item--color {
      .item-color {
        position: relative;
        box-shadow: none;

        &:before {
          @include center;
          z-index: 1;
          content: '';
          border: 1px solid palette(green, light);
          width: 57px;
          height: 57px;
          border-radius: map-get($border-radiuses, 'l');
        }
      }
    }
  }
}
</style>
